import { useEffect, useRef, useState } from "react";
import { AlertCircle, ChevronDown, ChevronRight, ExternalLink, FileText } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { generateInvoicePDF } from "@/lib/invoice-pdf";
import type { InvoiceData, InvoicePhotoMetadata } from "@shared/invoice";
import type { InvoiceLayoutCheck } from "@/hooks/use-invoice-layout-check";
import InvoiceLayoutWarnings from "./invoice-layout-warnings";

type InvoicePreviewProps = {
  data: InvoiceData;
  photos: InvoicePhotoMetadata[];
  invoiceId: string;
  photosLoading: boolean;
  photosError: boolean;
  disabled?: boolean;
  layoutCheck?: InvoiceLayoutCheck;
};

type PreviewState = "idle" | "waiting" | "generating" | "ready" | "error" | "photos-error";

export default function InvoicePreview({
  data,
  photos,
  invoiceId,
  photosLoading,
  photosError,
  disabled = false,
  layoutCheck,
}: InvoicePreviewProps) {
  const [open, setOpen] = useState(false);
  const [previewState, setPreviewState] = useState<PreviewState>("idle");
  const [pdfUrl, setPdfUrl] = useState<string | null>(null);
  const [errorMessage, setErrorMessage] = useState("");
  const [retryKey, setRetryKey] = useState(0);
  const requestId = useRef(0);
  const activeUrl = useRef<string | null>(null);

  useEffect(() => {
    if (!open) {
      setPreviewState("idle");
      return;
    }

    const thisRequest = ++requestId.current;
    if (activeUrl.current) {
      URL.revokeObjectURL(activeUrl.current);
      activeUrl.current = null;
    }
    setPdfUrl(null);
    setErrorMessage("");

    if (photosLoading) {
      setPreviewState("waiting");
      return () => {
        if (requestId.current === thisRequest) requestId.current += 1;
      };
    }
    if (photosError) {
      setPreviewState("photos-error");
      setErrorMessage("Die Fotoliste konnte nicht geladen werden. Laden Sie die Fotos erneut, bevor Sie die Vorschau öffnen.");
      return () => {
        if (requestId.current === thisRequest) requestId.current += 1;
      };
    }

    setPreviewState("waiting");
    const timer = window.setTimeout(() => {
      setPreviewState("generating");
      void generateInvoicePDF(data, photos, invoiceId, { preview: true })
        .then((blob) => {
          if (requestId.current !== thisRequest) return;
          const nextUrl = URL.createObjectURL(blob);
          activeUrl.current = nextUrl;
          setPdfUrl(nextUrl);
          setPreviewState("ready");
        })
        .catch((error: unknown) => {
          if (requestId.current !== thisRequest) return;
          setErrorMessage(error instanceof Error ? error.message : "Die PDF-Vorschau konnte nicht erstellt werden.");
          setPreviewState("error");
        });
    }, 700);

    return () => {
      window.clearTimeout(timer);
      if (requestId.current === thisRequest) requestId.current += 1;
    };
  }, [open, data, photos, invoiceId, photosLoading, photosError, retryKey]);

  useEffect(
    () => () => {
      requestId.current += 1;
      if (activeUrl.current) {
        URL.revokeObjectURL(activeUrl.current);
        activeUrl.current = null;
      }
    },
    [],
  );

  const closePreview = () => {
    requestId.current += 1;
    setOpen(false);
    if (activeUrl.current) {
      URL.revokeObjectURL(activeUrl.current);
      activeUrl.current = null;
    }
    setPdfUrl(null);
  };

  return (
    <Card>
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between gap-3">
          <CardTitle className="flex items-center gap-2 text-base text-bauwerk-teal">
            <FileText className="h-4 w-4" />
            PDF-Vorschau
          </CardTitle>
          <Button
            type="button"
            variant="outline"
            size="sm"
            aria-expanded={open}
            aria-controls="invoice-pdf-preview"
            disabled={disabled}
            onClick={() => (open ? closePreview() : setOpen(true))}
          >
            {open ? <ChevronDown className="mr-1.5 h-4 w-4" /> : <ChevronRight className="mr-1.5 h-4 w-4" />}
            {open ? "Vorschau schließen" : "Vorschau öffnen"}
          </Button>
        </div>
        <p className="text-sm leading-relaxed text-slate-600">
          Die Vorschau wird aus den aktuellen Eingaben erstellt und nicht gespeichert.
        </p>
      </CardHeader>

      {open && (
        <CardContent id="invoice-pdf-preview" className="pt-0">
          <div className="mb-3"><InvoiceLayoutWarnings check={layoutCheck} /></div>
          {previewState === "waiting" || previewState === "generating" ? (
            <div className="space-y-3" aria-live="polite" aria-busy="true">
              <div className="h-8 w-56 animate-pulse rounded bg-bauwerk-mint" />
              <div className="h-[min(70vh,48rem)] min-h-80 animate-pulse rounded-lg border border-slate-200 bg-slate-100" />
              <p className="text-sm text-slate-600">
                {photosLoading
                  ? "Fotoliste wird geladen…"
                  : previewState === "generating"
                    ? "PDF-Vorschau wird erstellt…"
                    : "Änderungen werden gesammelt…"}
              </p>
            </div>
          ) : previewState === "photos-error" || previewState === "error" ? (
            <div
              role="alert"
              className="flex flex-col items-start gap-3 rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-800"
            >
              <div className="flex items-start gap-2">
                <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
                <div>
                  <p className="font-semibold">
                    {previewState === "photos-error" ? "Fotos nicht verfügbar" : "Vorschau fehlgeschlagen"}
                  </p>
                  <p className="mt-1 leading-relaxed">{errorMessage}</p>
                </div>
              </div>
              {previewState === "error" && (
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => setRetryKey((key) => key + 1)}
                >
                  Erneut versuchen
                </Button>
              )}
            </div>
          ) : previewState === "ready" && pdfUrl ? (
            <div className="space-y-3">
              <div className="flex justify-end">
                <a
                  href={pdfUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1.5 rounded-md px-2 py-1 text-sm font-medium text-bauwerk-teal underline-offset-4 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-bauwerk-teal"
                >
                  <ExternalLink className="h-4 w-4" />
                  In neuem Tab öffnen
                </a>
              </div>
              <iframe
                src={pdfUrl}
                title="PDF-Vorschau der Rechnung"
                className="h-[min(75vh,56rem)] min-h-96 w-full rounded-lg border border-slate-200 bg-slate-100"
              />
              <p className="text-xs text-slate-500">
                Falls die Vorschau im Browser nicht angezeigt wird, öffnen Sie das PDF in einem neuen Tab.
              </p>
            </div>
          ) : null}
        </CardContent>
      )}
    </Card>
  );
}
